"use client";
import { useState } from "react";

import Link from "next/link";

import { Button } from "@heroui/button";

import Logo from "./Logo";

import { EventKeys } from "@/lib/observability/catalog";
import { EventPublisher } from "@/lib/observability/events";
import { routes } from "@/lib/routes";

export default function PublicHeader() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationLinks = [
    { href: "/", label: "Home" },
    { href: "/about", label: "About" },
    { href: "/how-it-works", label: "How It Works" },
    { href: "/use-cases", label: "Use Cases" },
    { href: "/features", label: "Features" },
    { href: "/pricing", label: "Pricing" },
    { href: "/contact", label: "Contact" },
  ];

  return (
    <header
      className="bg-white border-b border-gray-200 print:hidden"
      data-testid="public-header"
      role="banner"
    >
      {/* Skip to main content link - must have tabIndex for accessibility */}
      <a
        className="sr-only focus:not-sr-only focus:absolute focus:z-10 focus:p-4 focus:bg-white focus:text-black"
        data-testid="skip-link"
        href="#main-content"
        tabIndex={0}
      >
        Skip to main content
      </a>

      <nav
        aria-label="Main Navigation"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
        role="navigation"
      >
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link
              className="text-xl font-bold text-blue-600"
              href={routes.public.home()}
            >
              <Logo />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigationLinks.map((link) => (
              <Link
                key={link.href}
                className="text-gray-700 hover:text-blue-600 transition-colors"
                href={link.href}
                onClick={() => {
                  void EventPublisher.publishEvent(
                    EventKeys.PUBLIC_HEADER_NAV_LINK_CLICK,
                    {
                      href: link.href,
                      label: link.label,
                    },
                  );
                }}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <Link href={routes.auth.login()}>
              <Button
                size="sm"
                variant="light"
                onClick={() => {
                  void EventPublisher.publishEvent(
                    EventKeys.PUBLIC_HEADER_CTA_SIGNIN_CLICK,
                    {},
                  );
                }}
              >
                Sign In
              </Button>
            </Link>
            <Link href={routes.auth.register()}>
              <Button
                color="primary"
                size="sm"
                onClick={() => {
                  void EventPublisher.publishEvent(
                    EventKeys.PUBLIC_HEADER_CTA_GET_STARTED_CLICK,
                    {},
                  );
                }}
              >
                Get Started
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            aria-expanded={isMobileMenuOpen}
            aria-label="Toggle mobile menu"
            className="md:hidden p-2 rounded-md text-gray-700 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="mobile-menu-toggle"
            onClick={() => {
              setIsMobileMenuOpen(!isMobileMenuOpen);
              void EventPublisher.publishEvent(
                EventKeys.PUBLIC_HEADER_MOBILE_MENU_TOGGLE,
                {
                  open: !isMobileMenuOpen,
                },
              );
            }}
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d={
                  isMobileMenuOpen
                    ? "M6 18L18 6M6 6l12 12"
                    : "M4 6h16M4 12h16M4 18h16"
                }
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden" data-testid="mobile-menu">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-gray-200">
              {navigationLinks.map((link) => (
                <Link
                  key={link.href}
                  className="block px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md"
                  href={link.href}
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    void EventPublisher.publishEvent(
                      EventKeys.PUBLIC_HEADER_NAV_LINK_CLICK,
                      {
                        href: link.href,
                        label: link.label,
                      },
                    );
                  }}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 space-y-2">
                <Link href={routes.auth.login()}>
                  <Button
                    className="w-full"
                    size="sm"
                    variant="light"
                    onClick={() => {
                      void EventPublisher.publishEvent(
                        EventKeys.PUBLIC_HEADER_CTA_SIGNIN_CLICK,
                        {},
                      );
                    }}
                  >
                    Sign In
                  </Button>
                </Link>
                <Link href={routes.auth.register()}>
                  <Button
                    className="w-full"
                    color="primary"
                    size="sm"
                    onClick={() => {
                      void EventPublisher.publishEvent(
                        EventKeys.PUBLIC_HEADER_CTA_GET_STARTED_CLICK,
                        {},
                      );
                    }}
                  >
                    Get Started
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
